import React, { useEffect, useRef, useState } from 'react';
import './PaymentHistory.css';
import axios from 'axios';
import ENDPOINTS, { API_BASE_URL } from '../../../Constants/ApiEndpoints';
import AddPaymentComponent from '../../../CommonComponents/AddPaymentComponent';
import RefundComponent from '../../../CommonComponents/RefundComponent';
import PharmacyReceipt from '../../../CommonComponents/Invoice Receipts/PharmacyReceipt';
import { dateTimeFormatter2 } from '../../../CommonComponents/Utils/DateTimeFormatter';

const PaymentHistory = ({ patient }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedInvoiceId, setExpandedInvoiceId] = useState(null);
  const [prescribedMedicines, setPrescribedMedicines] = useState([]);

  const [showPaymentComponent, setShowPaymentComponent] = useState(false);
  const [showRefundComponent, setShowRefundComponent] = useState(false);
  const [showInvoiceComponent, setShowInvoiceComponent] = useState(false);

  const [selectedInvoice, setSelectedInvoice] = useState(null);
  // For printing invoices BEGIN
  const [printInvoice, setPrintInvoice] = useState(null);
  const componentRef = useRef();

  useEffect(() => {
    if (printInvoice) {
      // Delay printing slightly to ensure DOM update
      setTimeout(() => {
        if (componentRef.current) {
          window.print();
          setPrintInvoice(null); // Close the print view after printing
        }
      }, 100);
    }
  }, [printInvoice]);
  
  // Add a function to trigger print with selected invoice
  const handlePrintClick = (invoice, e) => {
    e.stopPropagation(); // prevent row expand
    setPrintInvoice(invoice);
  };


  // For printing invoices END
  const openPaymentComponent = (invoice) => {
    setSelectedInvoice(invoice);
    setShowPaymentComponent(true);
  };

  const closePaymentComponent = () => {
    setShowPaymentComponent(false);
    setSelectedInvoice(null);
  };

  const handlePaymentSubmit = (paymentData) => {
    console.log("Payment submitted for invoice:", selectedInvoice.invoiceId, paymentData);
    fetchInvoices(); // Reload the invoices after a successful payment
    // Optionally send to API here
  };

  
  const openRefundComponent = (invoice) => {
    setSelectedInvoice(invoice);
    setShowRefundComponent(true);
  };

  const closeRefundComponent = () => {
    setShowRefundComponent(false);
    setSelectedInvoice(null);
  };

  const handleRefundSubmit = (refundData) => {
    console.log("Payment submitted for invoice:", selectedInvoice.invoiceId, refundData);
    fetchInvoices(); // Reload the invoices after a successful payment
    // Optionally send to API here
  };

    
  // Fetch Invoices
  const fetchInvoices = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/getAllInvoicesOfaPrescription/prescription/${patient.prescriptionId}`
      );
      setInvoices(response.data);
    } catch (err) {
      setError(err.message || "Failed to fetch invoices");
    }
  };

  // Fetch Prescribed Medicines
  const fetchMedicines = async () => {
    try {
      const response = await axios.get(
        `${ENDPOINTS.GET_PRESCRIBED_MEDICINES_BY_PRESCRIPTIONID}/${patient.prescriptionId}`
      );
      setPrescribedMedicines(response.data);
    } catch (err) {
      setError(err.message || "Failed to fetch medicines");
    }
  };

  // useEffect to trigger both
  useEffect(() => {
    setLoading(true);
    setError(null);

    Promise.all([fetchInvoices(), fetchMedicines()])
      .finally(() => setLoading(false));
  }, [patient.prescriptionId]);


  const toggleInvoice = (invoiceId) => {
    setExpandedInvoiceId(prev => (prev === invoiceId ? null : invoiceId));
  };

  if (loading) return <p>Loading invoices...</p>;
  if (error) return <p>Error: {error}</p>;

  return (
    <div className="payment-history-container">
      <h2 className="payment-title">💳 Payments</h2>

      <div className="payment-header-row">
        <h3 className="payment-subheading">🧾 Payment History</h3>
        <div className="payment-actions">
          <button className="action-btn">📝 Add Notes</button>
          {/* <button className="action-btn">➕ Add Invoice</button> */}
          <button className="action-btn">🖨️ Print All</button>
        </div>
      </div>

      <div className="invoice-list">
        {invoices.map((inv, idx) => (
          <div key={idx}>
            <div
              className="invoice-row"
              onClick={() => toggleInvoice(inv.invoiceId)}
              style={{ cursor: 'pointer' }}
            >
              <div className="invoice-left">
                <span className="invoice-number">Invoice #{inv.invoiceId}</span>
                <span className="invoice-name">({inv.name || "Patient"})</span>
              </div>
              <div className="invoice-status">{inv.invoiceStatus}</div>
              <div className="invoice-right">
                <span className="invoice-amount">₹{inv.totalAmount}</span>

                <button
                  className="icon-btn" title="Add Payment"
                  onClick={(e) => {
                    e.stopPropagation(); // 👈 Prevents expansion
                    openPaymentComponent(inv);
                  }}
                >
                  💸
                </button>
                
                <button
                  className="icon-btn" title="Refund"
                  onClick={(e) => {
                    e.stopPropagation(); // 👈 Prevents expansion
                    openRefundComponent(inv);
                  }}

                >
                  🔁
                </button>

                <button className="icon-btn" title="Print" onClick={(e) => handlePrintClick(inv, e)}>🖨️</button>
                <button className="icon-btn">➕</button>
              </div>
            </div>

            {showPaymentComponent && selectedInvoice && (
              <AddPaymentComponent
                invoice={selectedInvoice}
                onClose={closePaymentComponent}
                onSubmit={handlePaymentSubmit}
              />
            )}

{showRefundComponent && selectedInvoice && (
              <RefundComponent
                invoice={selectedInvoice}
                onClose={closeRefundComponent}
                onSubmit={handleRefundSubmit}
              />
            )}

             {/* Render PharmacyReceipt only when printInvoice is set */}
             {printInvoice && (
              <div className="print-area">
                <PharmacyReceipt ref={componentRef} invoice={printInvoice} patient={patient} prescribedMedicines={prescribedMedicines} />
              </div>
            )}


            {expandedInvoiceId === inv.invoiceId && (
              <div className="invoice-details">
                <p><strong>Date:</strong> {dateTimeFormatter2(inv.invoiceTime)}</p>

                <table className="details-table">
                  <thead>
                    <tr>
                      <th>S.no</th>
                      <th>Item</th>
                      <th>Quantity</th>
                      <th>Expiry</th>
                      <th>Batch</th>
                      {/* <th>MRP</th> */}
                      <th>Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prescribedMedicines.length > 0 &&
                      prescribedMedicines.map((med, i) => (
                        <tr key={i}>
                          <td>{i + 1}</td>
                          <td>{(med.medicineBrand || '') + (med.composition ? ` (${med.composition})` : '')}</td>
                          <td>{med.quantity}</td>
                          <td>{med.expiryDate || '-'}</td>
                          <td>{med.batchNumber || '-'}</td>
                          {/* <td>₹{med.MRP}</td> */}
                          <td>₹{med.price}</td>
                        </tr>
                      ))}

                    {prescribedMedicines.length === 0 && (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center' }}>
                          No medicines prescribed
                        </td>
                      </tr>
                    )}
                  </tbody>

                </table>

                {/* {inv.payments && inv.payments.map((pmt, i) => (
                  <div key={i} className="payment-detail">
                    <p>
                      <strong>{pmt.paymentId}</strong> ₹{pmt.amount} ({pmt.paymentMode}) Received By <strong>{inv.name}</strong> at {new Date(pmt.paymentDate).toLocaleString()}
                    </p>
                  </div>
                ))} */}

                <div className="summary-box">
                  <div className="payment-line">
                    {inv.payments && inv.payments.map((pmt, i) => (
                      <div key={i}>
                        <strong>{pmt.paymentId}</strong> ₹{pmt.amount} ({pmt.paymentMode}) {pmt.amount < 0 ? <><strong style={{color: 'red'}}>Refunded</strong> By</> : <><strong style={{color: 'green'}}>Received</strong> By</>} <strong>{(inv.receivedBy || "Staff").toUpperCase()}</strong> at {dateTimeFormatter2(new Date(pmt.paymentDate).toLocaleString())} 
                      </div>
                    ))}
                  </div>

                  <div className="amount-summary">
                    <div className="summary-row">
                      <span>Total Amount:</span>
                      <span>₹{inv.totalAmount}</span>
                    </div>
                    <div className="summary-row">
                      <span>Paid Amount:</span>
                      <span>₹{inv.amountPaid}</span>
                    </div>
                    <div className="summary-row">
                      <span>Due:</span>
                      <span>₹{inv.invoiceStatus === 'FULLY_REFUNDED' ? 0 : (inv.totalAmount - inv.amountPaid)}</span>
                    </div>
                  </div>
                </div>


                <button className="cancel-btn" onClick={() => setExpandedInvoiceId(null)}>Collapse</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PaymentHistory;
